import WindowsEditionAvailability from "@/components/WindowsEditionAvailability";
import {
  ArrowRight,
  Mic,
  Monitor,
  Radar,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Video,
  Wand2,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ProductWordmark from "@/components/ProductWordmark";
import DeviceStory from "@/components/DeviceStory";
import { downloadStatusLabel } from "@/config/secureMacProStorefront";
import {
  SECUREMAC_AUDIT_DISCIPLINE,
  SECUREMAC_PRO_CHECK_CATEGORIES,
  SECUREMAC_PRIVACY_CONTROLS,
} from "@/config/secureMacMarketing";
import {
  SECUREMAC_PRO_CUSTOMER_PROMISE,
  SECUREMAC_PRO_EDITION_COMPARE,
  SECUREMAC_PRO_HERO_STRAPLINE,
  SECUREMAC_PRO_NETWORK_GUARD,
  SECUREMAC_PRO_PILLARS,
  SECUREMAC_PRO_SECURE_MY_MAC,
  SECUREMAC_PRO_UPDATES,
} from "@/config/secureMacProMarketing";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import { useSecureMacProStorefront } from "@/hooks/useSecureMacProStorefront";

const PILLAR_ICONS = { checks: ShieldCheck, privacy: SlidersHorizontal, "network-guard": Radar };
const PRIVACY_ICONS = { camera: Video, microphone: Mic, screenRecording: Monitor };

function DownloadAction({ storefront }) {
  const { account: customer, ready, owned } = useSecureMacProAccount();
  const launchHeld = !storefront.checkout_enabled;
  const label = launchHeld
    ? "Coming soon"
    : !ready
      ? "Get SecureShield Pro"
      : owned
        ? "Open account to download"
        : customer
          ? "Buy SecureShield Pro"
          : "Sign in to buy";
  const eyebrow = launchHeld ? "Not yet on sale" : owned ? "Ready in your account" : downloadStatusLabel(storefront);

  return (
    <Link
      className="block h-full border border-cyan-300 bg-cyan-300 px-5 py-6 text-left text-ink-900 transition-colors hover:border-white hover:bg-white"
      to="/account"
      data-testid="securemac-pro-download"
    >
      <span className="font-mono text-[9px] uppercase tracking-[.2em] opacity-70">{eyebrow}</span>
      <span className="mt-2 block font-display text-xl font-bold">{label}</span>
      <span className="mt-2 block text-sm opacity-75">{storefront.requirements.os} · {storefront.requirements.chip}</span>
    </Link>
  );
}

function SectionHeading({ eyebrow, title, body, light = false }) {
  return (
    <div className="max-w-3xl">
      <div className={`lf-eyebrow ${light ? "text-cyan-200" : ""}`}>{eyebrow}</div>
      <h2 className="mt-4 font-display text-4xl font-black leading-[1.02] md:text-5xl">{title}</h2>
      {body ? (
        <p className={`mt-5 text-lg leading-relaxed ${light ? "text-slate-200" : "text-ink-600"}`}>{body}</p>
      ) : null}
    </div>
  );
}

function TrustStrip({ storefront }) {
  const priceLabel = storefront.pricing_published ? storefront.price_usd : "";
  const items = [
    ["Supported platforms", `${storefront.requirements.os} · ${storefront.requirements.chip}`],
    ["What's inside", "Security checks · Privacy controls · Network Guard"],
    ["License", `${priceLabel ? `${priceLabel} · ` : ""}${storefront.license_model}`],
  ];

  return (
    <section aria-label="SecureShield Pro summary" className="border-y border-ink-100 bg-white">
      <div className="lf-container grid gap-px bg-ink-100 md:grid-cols-3">
        {items.map(([label, value]) => (
          <div key={label} className="bg-white px-6 py-6">
            <div className="font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{label}</div>
            <div className="mt-2 text-sm font-semibold leading-relaxed text-ink-900">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function buildFaq(storefront) {
  const priceLabel = storefront.pricing_published ? storefront.price_usd : "";
  return [
  [
    "What is SecureShield Pro?",
    "SecureShield Pro is SecureShield plus Network Guard: fourteen security checks with one-click fixes, quick Camera, Microphone and Screen Recording controls, and your own rules for the apps that connect.",
  ],
  [
    "What does it check?",
    `${storefront.checks_count} real macOS security settings across Essential Protection, macOS Integrity and Exposure — each read directly from the system, not inferred.`,
  ],
  [
    "What is Network Guard?",
    "A quiet watch over every connection your Mac makes. See which apps are reaching out and where, set rules to allow, block or ask first, and look back over recent activity — all without reading what you type or the pages you open.",
  ],
  [
    "What does Secure My Mac do?",
    "One click turns on your Firewall, Stealth Mode and Automatic Security Updates if any are off — and one click undoes it. Settings that could affect something you rely on, like Remote Login or Guest Account, stay as their own one-click switches.",
  ],
  [
    "How is SecureShield Pro different from SecureShield?",
    "SecureShield checks twelve settings. SecureShield Pro adds FileVault and Automatic Security Updates for fourteen in total, plus one-click fixes, Secure My Mac, Network Guard and your own connection rules. Both include the privacy controls.",
  ],
  [
    "How do updates work?",
    "Download SecureShield Pro from your LightningFlow account whenever you need to install or update it.",
  ],
  [
    "Does it work with Halo SuperVPN?",
    "Yes. Network Guard watches apps; Halo protects your path online. Different jobs, designed to sit together.",
  ],
  [
    "Can I buy SecureShield Pro today?",
    storefront.checkout_enabled
      ? `Yes. SecureShield Pro is a one-time ${priceLabel ? `${priceLabel} ` : ""}purchase. Buy once, then download it from your LightningFlow account.`
      : "SecureShield Pro is not available to buy at the moment. Check your account for purchase availability.",
  ],
  [
    "Which Macs does it support?",
    `${storefront.requirements.os}, ${storefront.requirements.chip}.`,
  ],
];
}

export default function SecureMacProProductPage() {
  const { storefront } = useSecureMacProStorefront();
  const faq = buildFaq(storefront);
  const priceLabel = storefront.pricing_published ? storefront.price_usd : "";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: storefront.product_name,
    applicationCategory: "SecurityApplication",
    operatingSystem: "macOS",
    description: SECUREMAC_PRO_CUSTOMER_PROMISE.description,
    ...(storefront.pricing_published
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: storefront.price_usd.replace(/[^0-9.]/g, ""),
            availability: storefront.checkout_enabled
              ? "https://schema.org/InStock"
              : "https://schema.org/PreOrder",
          },
        }
      : {}),
  };

  return (
    <div className="bg-white text-ink-900">
      <SEO
        title="SecureShield Pro — Network Guard for Your Mac."
        description={SECUREMAC_PRO_CUSTOMER_PROMISE.description}
        canonicalPath="/secureshield-pro"
        image="/secureshield/secure-shield-pro-official-share-2611d273.png"
        structuredData={structuredData}
      />

      <section className="lf-product-hero relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
        <div className="lf-container relative grid gap-12 py-16 md:py-24 lg:max-w-5xl">
          <div className="max-w-3xl">
            <ProductWordmark
              product="securemac-pro"
              size="hero"
              loading="eager"
              testId="securemac-pro-hero-wordmark"
            />
            <div className="lf-eyebrow text-cyan-200">{SECUREMAC_PRO_HERO_STRAPLINE}</div>
            <h1 className="mt-5 font-display text-4xl font-black leading-[.94] tracking-[-.04em] md:text-6xl">
              {SECUREMAC_PRO_CUSTOMER_PROMISE.headline}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
              {SECUREMAC_PRO_CUSTOMER_PROMISE.description}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
                href="#plans"
                data-testid="securemac-pro-cta-get"
              >
                {storefront.checkout_enabled ? "Get SecureShield Pro" : "Preview SecureShield Pro"} <ArrowRight className="h-4 w-4" />
              </a>
              <a
                className="lf-btn border border-white/40 text-white hover:border-white"
                href="#inside"
                data-testid="securemac-pro-cta-inside"
              >
                See what's inside
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip storefront={storefront} />

      <section id="inside" className="scroll-mt-24 lf-editorial-section">
        <div className="lf-container">
          <SectionHeading
            eyebrow="What's inside"
            title="Three kinds of protection. One quiet app."
            body="SecureShield checks your Mac's security settings and puts your privacy controls a tap away. SecureShield Pro adds Network Guard, so you can see which apps connect and decide what's allowed."
          />
          <div className="mt-12 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-3">
            {SECUREMAC_PRO_PILLARS.map((pillar) => {
              const Icon = PILLAR_ICONS[pillar.id];
              return (
                <a key={pillar.id} href={pillar.href} className="group bg-white p-7 transition-colors hover:bg-ink-50 md:p-9" data-testid={`securemac-pro-pillar-${pillar.id}`}>
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-electric" strokeWidth={1.6} />
                    {pillar.badge ? (
                      <span className="rounded-full border border-electric/30 bg-electric/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[.18em] text-electric">{pillar.badge}</span>
                    ) : null}
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-black">{pillar.title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-600">{pillar.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-ink-900 group-hover:text-electric">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section id="checks" className="scroll-mt-24 border-y border-ink-100 bg-[#031124] text-white">
        <div className="lf-container grid gap-12 py-20 md:py-28 lg:grid-cols-[1.2fr_.8fr] lg:items-start">
          <div>
            <SectionHeading
              light
              eyebrow="Security checks"
              title="Fourteen settings. Every one explained."
              body="SecureShield reads the real state of the settings that protect your Mac, tells you plainly what needs attention, and fixes it when you ask."
            />
            <div className="mt-8 space-y-3">
              {SECUREMAC_PRO_CHECK_CATEGORIES.map(([number, title, body]) => (
                <div key={number} className="grid grid-cols-[44px_1fr] gap-4 border-t border-white/15 py-4">
                  <span className="font-mono text-xs text-cyan-200">{number}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-300">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <aside className="border border-cyan-300/40 bg-cyan-300/[.06] p-7 md:p-8" data-testid="securemac-pro-secure-my-mac">
            <Wand2 className="h-6 w-6 text-cyan-300" strokeWidth={1.6} />
            <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-cyan-200">One click</div>
            <h3 className="mt-2 font-display text-3xl font-black">{SECUREMAC_PRO_SECURE_MY_MAC.title}</h3>
            <p className="mt-4 leading-relaxed text-slate-200">{SECUREMAC_PRO_SECURE_MY_MAC.body}</p>
          </aside>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
            <SectionHeading eyebrow={SECUREMAC_AUDIT_DISCIPLINE.method} title={SECUREMAC_AUDIT_DISCIPLINE.title} />
            <p className="max-w-2xl text-lg leading-relaxed text-ink-600">{SECUREMAC_AUDIT_DISCIPLINE.test_note}</p>
          </div>
          <ol className="mt-12 grid divide-y divide-ink-100 border border-ink-900 bg-white px-5 py-2 md:grid-cols-4 md:divide-x md:divide-y-0 md:px-7">
            {SECUREMAC_AUDIT_DISCIPLINE.steps.map((step, index) => (
              <li key={step.label} className="py-5 md:px-5 md:py-7">
                <span className="font-mono text-xs text-electric">0{index + 1}</span>
                <div className="mt-2 font-display text-lg font-bold">{step.label}</div>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="privacy-controls" className="scroll-mt-24 lf-editorial-section border-b border-ink-100 bg-white" data-testid="securemac-pro-privacy-controls">
        <div className="lf-container grid gap-12 lg:grid-cols-[.72fr_1.28fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow={SECUREMAC_PRIVACY_CONTROLS.eyebrow}
              title={SECUREMAC_PRIVACY_CONTROLS.title}
              body={SECUREMAC_PRIVACY_CONTROLS.body}
            />
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-ink-500">{SECUREMAC_PRIVACY_CONTROLS.honesty}</p>
          </div>
          <div className="grid gap-px border border-ink-100 bg-ink-100 sm:grid-cols-3">
            {SECUREMAC_PRIVACY_CONTROLS.controls.map((control) => {
              const Icon = PRIVACY_ICONS[control.id];
              return (
                <article key={control.id} className="bg-white p-6 md:p-7">
                  <Icon className="h-5 w-5 text-electric" strokeWidth={1.6} />
                  <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">One tap</div>
                  <h3 className="mt-2 font-display text-xl font-bold text-ink-900">{control.label}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-600">{control.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="network-guard" className="scroll-mt-24 bg-[#031124] text-white">
        <div className="lf-container py-20 md:py-28">
          <div className="grid gap-12 lg:max-w-5xl">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-300/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[.2em] text-cyan-200">
                <Radar className="h-3.5 w-3.5" strokeWidth={2} />
                {SECUREMAC_PRO_NETWORK_GUARD.eyebrow} · SecureShield Pro
              </span>
              <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] md:text-6xl">{SECUREMAC_PRO_NETWORK_GUARD.headline}</h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">{SECUREMAC_PRO_NETWORK_GUARD.description}</p>
            </div>
          </div>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 xl:grid-cols-3">
            {SECUREMAC_PRO_NETWORK_GUARD.features.map((item) => (
              <article key={item.title} className="bg-[#031124] p-7">
                <h3 className="font-display text-2xl font-bold">{item.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-300">{item.body}</p>
              </article>
            ))}
            <article
              className="flex min-h-[14rem] items-center justify-center bg-[#031124] p-8 md:p-10 xl:col-span-2"
              aria-label="SecureShield Pro"
              data-testid="securemac-pro-network-guard-wordmark"
            >
              <ProductWordmark product="securemac-pro" size="grid" />
            </article>
          </div>
        </div>
      </section>


      <section id="updates" className="scroll-mt-24 lf-editorial-section border-b border-ink-100 bg-white" data-testid="securemac-pro-updates">
        <div className="lf-container">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-ink-50 px-3 py-1 font-mono text-[10px] uppercase tracking-[.2em] text-ink-600">
                <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
                {SECUREMAC_PRO_UPDATES.eyebrow} · SecureShield Pro
              </span>
              <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] md:text-5xl">{SECUREMAC_PRO_UPDATES.headline}</h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-600">{SECUREMAC_PRO_UPDATES.description}</p>
            </div>
            <div className="grid gap-px border border-ink-100 bg-ink-100 sm:grid-cols-2">
              {SECUREMAC_PRO_UPDATES.features.map((item) => (
                <article key={item.title} className="bg-white p-7">
                  <h3 className="font-display text-2xl font-bold text-ink-900">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-600">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="lf-editorial-section border-b border-ink-100">
        <div className="lf-container">
          <SectionHeading
            eyebrow="Two editions"
            title="Choose the SecureShield that fits."
            body="Start with SecureShield. Add one-click fixes and Network Guard, and it becomes SecureShield Pro."
          />
          <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
            {SECUREMAC_PRO_EDITION_COMPARE.map((edition) => (
              <article key={edition.label} className="bg-white p-7 md:p-9">
                <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">{edition.tagline}</div>
                <h3 className="mt-4 font-display text-3xl font-black">{edition.label}</h3>
                <dl className="mt-6 space-y-4 text-sm leading-relaxed text-ink-600">
                  {[
                    ["Security checks", edition.checks],
                    ["Fixes", edition.fixes],
                    ["Privacy controls", edition.privacy],
                    ["Network Guard", edition.network_guard],
                    ["Updates", edition.updates],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-400">{label}</dt>
                      <dd className="mt-1 text-ink-800">{value}</dd>
                    </div>
                  ))}
                </dl>
                {edition.label === "SecureShield" ? (
                  <Link className="lf-btn-secondary mt-8" to="/secureshield" data-testid="securemac-pro-related-securemac">
                    Explore SecureShield <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a className="lf-btn-primary mt-8" href="#plans" data-testid="securemac-pro-compare-cta">
                    Get SecureShield Pro <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="plans" className="scroll-mt-24 bg-[#031124] text-white">
        <div className="lf-container py-16 md:py-20">
          <SectionHeading
            eyebrow="Get SecureShield Pro"
            title="One purchase. No subscription."
            body={`${priceLabel ? `${priceLabel}, once. ` : ""}Buy it, then download it from your LightningFlow account.`}
            light
          />
          <div className="mt-10 grid gap-3 sm:max-w-md">
            <DownloadAction storefront={storefront} />
            <WindowsEditionAvailability product="securemac-pro" />
          </div>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
            Prices in USD. {storefront.launch_summary}
          </p>
        </div>

        <div className="border-y border-ink-200 bg-[#f3f3f5] text-ink-900">
          <div className="lf-container grid sm:grid-cols-3">
            {[
              { value: priceLabel || "One-time", label: "USD one-time" },
              { value: "0", label: "Subscriptions required" },
              { value: "3", label: "Kinds of protection in one app" },
            ].map((item) => (
              <article key={item.label} className="border-b border-ink-200 px-6 py-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 md:px-8">
                <div className="font-display text-3xl font-black tracking-[-.03em]">{item.value}</div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[.24em] text-ink-600">{item.label}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <SectionHeading
            eyebrow="From the same team"
            title="Pair it with Halo."
            body="SecureShield Pro looks after the Mac. Halo SuperVPN looks after the path online."
          />
          <div className="mt-10 max-w-xl border border-ink-100 bg-white p-7 md:p-9">
            <h3 className="font-display text-3xl font-black">Halo SuperVPN</h3>
            <p className="mt-4 leading-relaxed text-ink-600">
              Private, high-performance protection powered by LightningFlow’s QuantumCloak security feature.
            </p>
            <Link className="lf-btn-secondary mt-7" to="/halo" data-testid="securemac-pro-related-halo">
              Discover Halo SuperVPN <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <DeviceStory product="securemac-pro" />

      <section className="lf-section">
        <div className="lf-container grid gap-12 lg:grid-cols-[.65fr_1.35fr]">
          <SectionHeading eyebrow="Questions" title="Clear before you buy." />
          <div className="divide-y divide-ink-100 border border-ink-100 bg-white">
            {faq.map(([question, answer]) => (
              <details key={question} className="group p-6">
                <summary className="cursor-pointer list-none pr-8 font-display text-lg font-bold marker:hidden">{question}</summary>
                <p className="mt-4 max-w-3xl leading-relaxed text-ink-600">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-editorial-section lf-editorial-dark" data-testid="securemac-pro-final-cta">
        <div className="lf-container grid gap-10 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div>
            <p className="lf-eyebrow text-cyan-200">Get SecureShield Pro</p>
            <h2 className="mt-5 max-w-5xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">
              See what's connecting. Then decide.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              Security checks, privacy controls and Network Guard. One app, one price.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white" href="#plans">
              Get SecureShield Pro <ArrowRight className="h-4 w-4" />
            </a>
            <Link className="lf-btn border border-white/35 text-white hover:border-white" to="/halo-fortress#compare" data-testid="securemac-pro-compare-editions">Compare editions</Link>
            <Link className="lf-btn border border-white/35 text-white hover:border-white" to="/halo-fortress" data-testid="securemac-pro-discover-fortress">Discover Fortress</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
